import {useMutation, useQuery} from '@tanstack/react-query';
import * as R from 'ramda';
import {pipe} from '@practicegenius/pg-util/src/FunctionUtils';
import {UnknownServerData} from '@practicegenius/http-clients-js/src/types';
import {z} from 'zod';
import {
  Practice,
  PracticeOffice,
  PracticeStatus,
  PracticeType,
} from '@practicegenius/domain/src/data/practice';
import {objectKeysToCamelCaseDeep} from '@practicegenius/pg-util/src/ObjectUtils';
import {isNotNil} from '@practicegenius/pg-util/src/TypescriptUtils';
import {getLogoUrl} from '../../utils/practice';
import {sitesHttpClient, useApi} from '../../http/AuthApiProvider';
import {useActivePractice} from '../hooks/useActivePractice';

export type UnauthenticatedContext = {
  // This is currently an old representation of the practice because it is sourced from the legacy API.
  // In the long run, this will eventually be provided via the modern API and will become a
  //  more common, shared representation of the practice.
  practice: {
    practiceId: number;
    practiceUri: string;
    practiceName: null | string;
    practiceType: PracticeType;
    website: Practice['website'] | null;
    address1: PracticeOffice['addressLine1'] | null;
    address2: PracticeOffice['addressLine2'] | null;
    city: PracticeOffice['city'] | null;
    state: PracticeOffice['state'] | null;
    zip: PracticeOffice['zip'] | null;
    country: PracticeOffice['country'] | null;
    phone: Practice['website'] | null;
    fax: string | null;
    email: Practice['email'] | null;
    logo1: string | null;
    logo2: string | null;
    dashBanner: string | null;
    rewardCard: string | null;
    timeZone: string;
    logo3: string | null;
    status: PracticeStatus;
    appointmentsStatus: boolean;
  };
};

const parseLogoUrl = R.when(isNotNil, getLogoUrl);

export const useUnauthenticatedContextQuery = () => {
  return useQuery({
    queryKey: ['global-unathenticated-context'],
    queryFn: () =>
      sitesHttpClient._unsafe.get('/api/unauthenticated_context').then(
        pipe<UnauthenticatedContext>(
          R.prop('data'),
          objectKeysToCamelCaseDeep,
          R.evolve({
            practice: {
              logo1: parseLogoUrl,
              logo2: parseLogoUrl,
              logo3: parseLogoUrl,
              dashBanner: parseLogoUrl,
            },
          }),
        ),
      ),
  });
};

/** If there's a referrer (post-login page) in the session, clear it */
export const useClearReferrerInSession = () =>
  useMutation({
    mutationFn: () => sitesHttpClient._unsafe.get('/api/session/clearreferrer'),
  });

/** This sets a session variable in the session which the backends uses to bypass recaptcha validation.
 *  The hook name is intentionally vague to make it less clear what it does if build process doesn't change name.
 * This should be removed once the recaptcha feature is expected to be enabled on all versions of mobile app */
export const useSyncSession = () =>
  useMutation({
    mutationKey: ['sync-session'],
    mutationFn: () => sitesHttpClient._unsafe.post('/api/sync-session'),
  });

const MemberSchema = z.object({
  id: z.number(),
  firstname: z.string(),
  lastname: z.string(),
  userRole: z.enum(['doctor', 'staff']),
  image: z.string(),
  bio: z.string().nullable(),
});

export const AboutUsDataSchema = z.object({
  rewardCard: z.object({
    url: z.string(),
  }),
  doctors: z.array(MemberSchema),
  staff: z.array(MemberSchema),
  aboutUs: z.string().nullable(),
});

export type Member = z.infer<typeof MemberSchema>;

export const useGetAboutUs = () => {
  const practice = useActivePractice();
  const {coreAuth} = useApi();
  return useQuery({
    queryKey: ['about-us', practice?.id],
    queryFn: () =>
      coreAuth._unsafe
        .get(`practices/${practice?.id}/about-us`)
        .then(({data}: {data: UnknownServerData}) => {
          const parsedData = objectKeysToCamelCaseDeep(data.data);
          return AboutUsDataSchema.parse(parsedData);
        }),
  });
};
